import type { Account, AccountLedgerEntry, Plan, PlanPayment, Transaction } from '../types';
import { addDays, addMonths, monthKey, normalizeDateISO, todayISO } from './date';

export type DateQuickFilter = 'Today' | 'Yesterday' | 'This Week' | 'This Month' | 'Last Month' | 'This Financial Year' | 'Last Financial Year' | 'Custom';
export type AccountFilter = 'all' | 'unassigned' | 'liquid' | 'credit_cards' | string;

export const dateQuickFilters: DateQuickFilter[] = ['Today', 'Yesterday', 'This Week', 'This Month', 'Last Month', 'This Financial Year', 'Last Financial Year', 'Custom'];

const startOfMonth = (date: string) => `${date.slice(0, 7)}-01`;
const endOfMonth = (date: string) => addDays(addMonths(startOfMonth(date), 1), -1);

export const rangeFromQuickFilter = (filter: DateQuickFilter, now = todayISO()) => {
  const today = normalizeDateISO(now);
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  if (filter === 'Today') return { from: today, to: today };
  if (filter === 'Yesterday') {
    const yesterday = addDays(today, -1);
    return { from: yesterday, to: yesterday };
  }
  if (filter === 'This Week') {
    const day = new Date(today + 'T00:00:00').getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    return { from: addDays(today, mondayOffset), to: today };
  }
  if (filter === 'Last Month') {
    const lastMonth = addMonths(startOfMonth(today), -1);
    return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
  }
  if (filter === 'This Financial Year') {
    const fyStartYear = month >= 4 ? year : year - 1;
    return { from: `${fyStartYear}-04-01`, to: `${fyStartYear + 1}-03-31` };
  }
  if (filter === 'Last Financial Year') {
    const fyStartYear = (month >= 4 ? year : year - 1) - 1;
    return { from: `${fyStartYear}-04-01`, to: `${fyStartYear + 1}-03-31` };
  }
  return { from: startOfMonth(today), to: endOfMonth(today) };
};

export const inDateRange = (date: string | undefined, from: string, to: string) => {
  const iso = normalizeDateISO(date);
  return iso >= from && iso <= to;
};

export const accountFilterLabel = (filter: AccountFilter, accounts: Account[]) => {
  if (filter === 'all') return 'All Accounts';
  if (filter === 'unassigned') return 'Unassigned';
  if (filter === 'liquid') return 'Bank/Cash/Wallet';
  if (filter === 'credit_cards') return 'Credit Cards';
  return accounts.find((account) => account.id === filter)?.name || 'Specific account';
};

export const accountMatches = (filter: AccountFilter, row: { accountId?: string; accountType?: string }) => {
  if (filter === 'all') return true;
  if (filter === 'unassigned') return !row.accountId;
  if (filter === 'liquid') return Boolean(row.accountId) && row.accountType !== 'credit_card';
  if (filter === 'credit_cards') return row.accountType === 'credit_card';
  return row.accountId === filter;
};

export const paymentAccountMatches = (filter: AccountFilter, row: PlanPayment) => {
  if (filter === 'all') return true;
  if (filter === 'unassigned') return !row.paidFromAccountId;
  if (filter === 'liquid') return Boolean(row.paidFromAccountId) && row.paidFromAccountType !== 'credit_card';
  if (filter === 'credit_cards') return row.paidFromAccountType === 'credit_card';
  return row.paidFromAccountId === filter;
};

export const ledgerAccountMatches = (filter: AccountFilter, row: AccountLedgerEntry, accounts: Account[]) => {
  if (filter === 'all') return true;
  if (filter === 'unassigned') return false;
  const account = accounts.find((item) => item.id === row.accountId);
  if (filter === 'liquid') return account?.type !== 'credit_card';
  if (filter === 'credit_cards') return account?.type === 'credit_card';
  return row.accountId === filter;
};

export const planInRange = (plan: Plan, from: string, to: string) => inDateRange(plan.nextDueDate || plan.dueDate, from, to);
export const budgetInRange = (budgetMonthKey: string, from: string, to: string) => budgetMonthKey >= monthKey(from) && budgetMonthKey <= monthKey(to);

